import { useEffect, useMemo, useState, Fragment } from "react";
import { useSearchParams, Link } from "react-router-dom";
import Layout from "../components/Layout";
import { WmPart, getWmPartMapSync, wmPartNo } from "../lib/wmParts";
import api from "../lib/api";
import { fmtCur, fmtNum, fmtDate } from "../lib/format";
import { useCurrencyMode } from "../lib/useCurrencyMode";
import { CaretRight, CaretDown, MagnifyingGlass, X, ArrowsClockwise } from "@phosphor-icons/react";
import { toast } from "sonner";
import PoDetailModal from "../components/PoDetailModal";
import { EditedPill, latestLineEdit } from "../components/EditedPill";
import { ExportXlsxButton } from "../components/ExportXlsxButton";
import { xlsxDate } from "../lib/exportXlsx";

// Target Date = customer due date − per-customer ex-factory offset (days). When no offset
// is configured, target = due. Mirrors the Target Date logic on the Purchase Orders page.
const targetFromDue = (deliv, offsetDays) => {
  if (!deliv) return null;
  const d = new Date(deliv);
  if (isNaN(d.getTime())) return null;
  const off = Number(offsetDays) || 0;
  if (!off) return deliv;
  d.setDate(d.getDate() - off);
  return d.toISOString();
};

export default function MsOpenOrders() {
  const [params, setParams] = useSearchParams();
  const [rows, setRows] = useState([]);
  const [intakePos, setIntakePos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [initialLoaded, setInitialLoaded] = useState(false);
  const [plants, setPlants] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [expanded, setExpanded] = useState(new Set());
  const [groupByCustPo, setGroupByCustPo] = useState(false);
  const [sort, setSort] = useState({ key: "po_date", dir: "desc" });
  const [viewPo, setViewPo] = useState(null);
  const [viewLoading, setViewLoading] = useState(false);
  const openPoModal = async (pono) => {
    setViewLoading(true); setViewPo(null);
    try {
      const r = await api.get(`/ms/pos/${pono}`);
      setViewPo(r.data);
    } catch (e) {
      setViewPo(null);
    } finally { setViewLoading(false); }
  };
  const dateField = "po_date";
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const mode = useCurrencyMode();

  const plant = params.get("plant") || "";
  const customer = params.get("customer") || "";
  const search = params.get("q") || "";

  const reloadRows = () => {
    // customer + currency-mode are filtered client-side (see grouped memo) — only plant hits the
    // server so the cache-warmed dataset is reused and filter switches stay instant.
    const qs = new URLSearchParams();
    if (plant) qs.set("plant", plant);
    api.get(`/ms/open-orders?${qs.toString()}`).then((r) => setRows(r.data)).catch(() => {});
  };

  // Per-line ERP re-sync (retry when a write-back failed). Field edits are done from the PO detail popup.
  const [resyncKey, setResyncKey] = useState(null);
  const resyncLine = async (pono, li) => {
    setResyncKey(`${pono}-${li}`);
    try {
      const r = await api.post(`/ms/pos/${pono}/line-erp-sync`, { line_index: li });
      if (r.data?.ok) toast.success("Line pushed to live ERP \u2713 (due date, qty, price)");
      else {
        const bad = Object.entries(r.data?.results || {})
          .filter(([, v]) => !v.matched).map(([k, v]) => `${k}: ${v.reason || "unmatched"}`).join("; ");
        toast.error(`ERP sync incomplete \u2014 ${bad}`);
      }
      reloadRows();
    } catch (e) {
      toast.error(e.response?.data?.detail || "ERP sync failed (admin only)");
    } finally { setResyncKey(null); }
  };

  useEffect(() => {
    api.get("/ms/plants").then((r) => setPlants(r.data)).catch(() => {});
    api.get("/ms/customers").then((r) => setCustomers(r.data)).catch(() => {});
    // Confirmed intake POs (Mongo) are merged into this open-orders view, tagged as "Intake".
    api.get("/po", { params: { status: "confirmed" } })
      .then((r) => setIntakePos(r.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    // customer + currency-mode are NOT sent to the server — we fetch the full (cache-warmed) plant
    // dataset once and filter by customer + mode CLIENT-SIDE, so switching them is instant.
    const qs = new URLSearchParams();
    if (plant) qs.set("plant", plant);
    api.get(`/ms/open-orders?${qs.toString()}`).then((r) => setRows(r.data)).finally(() => { setLoading(false); setInitialLoaded(true); });
  }, [plant]);

  const setParam = (k, v) => {
    const np = new URLSearchParams(params);
    if (v) np.set(k, v); else np.delete(k);
    setParams(np);
  };

  // Flatten confirmed intake POs (Mongo) into the SAP open-orders row shape so both
  // live in one grouped view. Only open lines (qty_open > 0) are surfaced here.
  // Intake POs carry no SAP plant/pcode, so hide them when those filters are active.
  const intakeRows = useMemo(() => {
    if (plant || customer) return [];
    // Dedup guard: never merge an intake PO that is ALSO already surfaced as a live ERP open-order
    // row (same Customer-PO or internal PO#) — otherwise its qty/value would be double-counted.
    const norm = (x) => String(x || "").trim().toUpperCase().replace(/\s+/g, "");
    const erpSeen = new Set();
    for (const r of rows) { erpSeen.add(norm(r.ms_custpo)); erpSeen.add(norm(r.ms_pono)); }
    const out = [];
    for (const p of intakePos) {
      const cp = norm(p.customer_po || p.po_number);
      const pn = norm(p.po_number);
      if ((cp && erpSeen.has(cp)) || (pn && erpSeen.has(pn))) continue;
      for (const l of p.line_items || []) {
        const ord = Number(l.quantity_ordered) || 0;
        const shp = Number(l.quantity_shipped) || 0;
        const open = Math.max(0, ord - shp);
        if (open <= 0) continue;
        const price = Number(l.unit_price) || 0;
        out.push({
          _source: "intake",
          _intake_id: p.id,
          ms_pono: p.po_number,
          ms_custpo: p.customer_po || p.po_number,
          cust_po2: p.cust_po2 || "",
          customer_name: p.customer_name,
          ms_pcode: p.resolved_pcode || "—",
          po_date: p.order_date,
          cust_po_date: p.order_date,
          currency: p.currency || "USD",
          currency_code: p.currency || "USD",
          rev_no: p.is_change_order ? 1 : 0,
          icode: l.sku || "",
          name: l.description || "",
          deliv_date: l.due_date || null,
          qty_ordered: ord,
          qty_shipped: shp,
          qty_open: open,
          open_value: open * price,
        });
      }
    }
    return out;
  }, [intakePos, rows, plant, customer]);

  // Group lines by PO
  const grouped = useMemo(() => {
    const map = new Map();
    const cust = customer.trim().toLowerCase();
    const m = (mode || "").trim().toLowerCase();
    const modeOk = (cc) => {
      if (!m) return true;
      cc = (cc || "INR").toUpperCase();
      if (m === "usd" || m === "gbp" || m === "inr") return cc === m.toUpperCase();
      if (m === "export") return cc === "USD" || cc === "GBP";
      if (m === "domestic") return cc === "INR";
      return true;
    };
    const filter = (r) => {
      // client-side customer filter (mirrors the server's ms_pcode / customer_name match)
      if (cust && (r.ms_pcode || "").toLowerCase() !== cust && (r.customer_name || "").toLowerCase() !== cust) return false;
      // client-side currency-mode filter (mirrors server _mode_filter on currency_code)
      if (!modeOk(r.currency_code)) return false;
      if (!search) return true;
      const s = search.toLowerCase();
      return (
        String(r.ms_pono).includes(s) ||
        (r.ms_custpo || "").toLowerCase().includes(s) ||
        (r.cust_po2 || "").toLowerCase().includes(s) ||
        (r.customer_name || "").toLowerCase().includes(s) ||
        (r.ms_pcode || "").toLowerCase().includes(s) ||
        (r.icode || "").toLowerCase().includes(s) ||
        (r.name || "").toLowerCase().includes(s) ||
        wmPartNo(getWmPartMapSync(), r.icode).toLowerCase().includes(s)
      );
    };
    for (const r of [...rows, ...intakeRows].filter(filter)) {
      // Group key: internal PO # (default) or the shared Customer PO (toggle).
      const gk = groupByCustPo ? (r.ms_custpo || r.ms_pono) : r.ms_pono;
      if (!map.has(gk)) {
        map.set(gk, {
          group_id: gk,
          ms_pono: r.ms_pono, ms_custpo: r.ms_custpo,
          cust_po2: r.cust_po2 || "",
          customer_name: r.customer_name, ms_pcode: r.ms_pcode,
          po_date: r.po_date, cust_po_date: r.cust_po_date,
          currency: r.currency, rev_no: r.rev_no,
          _source: r._source || "sap", _intake_id: r._intake_id,
          ponos: new Set(),
          lines: [],
          tot_ordered: 0, tot_shipped: 0, tot_open: 0, tot_value: 0,
        });
      }
      const g = map.get(gk);
      if (!g.cust_po2 && r.cust_po2) g.cust_po2 = r.cust_po2;
      g.ponos.add(r.ms_pono);
      // keep the earliest PO date across releases for the group header
      if (r.po_date && (!g.po_date || String(r.po_date) < String(g.po_date))) g.po_date = r.po_date;
      g.lines.push(r);
      g.tot_ordered += r.qty_ordered;
      g.tot_shipped += r.qty_shipped;
      g.tot_open += r.qty_open;
      g.tot_value += r.open_value;
      g.currency_code = r.currency_code || g.currency_code || "INR";
    }
    return Array.from(map.values()).sort((a, b) => (b.po_date || "").localeCompare(a.po_date || ""));
  }, [rows, intakeRows, search, groupByCustPo, customer, mode]);

  // date-range filter + sortable headers on the PO (master) rows
  const view = useMemo(() => {
    let out = grouped;
    if (dateField && (dateFrom || dateTo)) {
      const fromMs = dateFrom ? Date.parse(dateFrom + "T00:00:00") : -Infinity;
      const toMs = dateTo ? Date.parse(dateTo + "T23:59:59") : Infinity;
      out = out.filter((g) => {
        const ms = Date.parse(g[dateField]);
        return !isNaN(ms) && ms >= fromMs && ms <= toMs;
      });
    }
    const dir = sort.dir === "asc" ? 1 : -1;
    const num = new Set(["lines", "tot_ordered", "tot_shipped", "tot_open", "tot_value"]);
    return [...out].sort((a, b) => {
      const va = sort.key === "lines" ? a.lines.length : a[sort.key];
      const vb = sort.key === "lines" ? b.lines.length : b[sort.key];
      if (va == null) return 1; if (vb == null) return -1;
      if (num.has(sort.key)) return ((va || 0) - (vb || 0)) * dir;
      const da = Date.parse(va), db2 = Date.parse(vb);
      if (!isNaN(da) && !isNaN(db2) && /^\d{4}-\d{2}-\d{2}/.test(String(va))) return (da - db2) * dir;
      return String(va).localeCompare(String(vb), undefined, { numeric: true }) * dir;
    });
  }, [grouped, sort, dateField, dateFrom, dateTo]);

  const toggleSort = (key) => setSort((s) => (s.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" }));
  const SortTh = ({ k, children, align }) => (
    <th onClick={() => toggleSort(k)} style={{ cursor: "pointer", textAlign: align, whiteSpace: "nowrap", userSelect: "none" }} data-testid={`oo-th-${k}`}>
      {children}{sort.key === k ? <span style={{ marginLeft: 3, opacity: 0.7 }}>{sort.dir === "asc" ? "▲" : "▼"}</span> : null}
    </th>
  );

  const totalOpen = view.reduce((s, g) => s + g.tot_open, 0);
  const totalLines = view.reduce((s, g) => s + g.lines.length, 0);
  // Open value must NOT be summed across currencies — group subtotals by currency so a
  // USD PO and an INR PO are never added into one meaningless figure.
  const valueByCurrency = view.reduce((acc, g) => {
    const c = (g.currency_code || "INR").toUpperCase();
    acc[c] = (acc[c] || 0) + (g.tot_value || 0);
    return acc;
  }, {});
  const currencyTotals = Object.entries(valueByCurrency).sort((a, b) => b[1] - a[1]);

  const toggle = (id) => {
    const n = new Set(expanded);
    if (n.has(id)) n.delete(id); else n.add(id);
    setExpanded(n);
  };

  const expandAll = () => setExpanded(new Set(view.map((g) => g.group_id)));
  const collapseAll = () => setExpanded(new Set());

  // Item-level export: one row per open line item (PO header fields repeated on each line)
  // so users get a true item-level report instead of PO-group headers only.
  const exportRows = view.flatMap((g) => (g.lines || []).map((ln) => ({ g, ln })));
  const exportColumns = [
    { header: "Customer PO", value: ({ g, ln }) => ln.ms_custpo || g.ms_custpo || "" },
    { header: "End Customer PO Number", value: ({ g, ln }) => ln.cust_po2 || g.cust_po2 || "" },
    { header: "PO #", value: ({ ln }) => ln.ms_pono || "" },
    { header: "PO Date", value: ({ g, ln }) => xlsxDate(ln.po_date || g.po_date) },
    { header: "Customer", value: ({ g, ln }) => ln.customer_name || g.customer_name || "" },
    { header: "Item #", value: ({ ln }) => ln.icode || "" },
    { header: "WM Part #", value: ({ ln }) => wmPartNo(getWmPartMapSync(), ln.icode) },
    { header: "Description", value: ({ ln }) => ln.name || "" },
    { header: "Ordered", value: ({ ln }) => Number(ln.qty_ordered) || 0 },
    { header: "Shipped", value: ({ ln }) => Number(ln.qty_shipped) || 0 },
    { header: "Open", value: ({ ln }) => Number(ln.qty_open) || 0 },
    { header: "Unit Price", value: ({ ln }) => (ln.price != null ? Number(ln.price) : "") },
    { header: "Open Value", value: ({ ln }) => Number(ln.open_value) || 0 },
    { header: "Currency", value: ({ g, ln }) => (ln.currency_code || g.currency_code || "INR").toUpperCase() },
    { header: "Due Date", value: ({ ln }) => xlsxDate(ln.deliv_date) },
    { header: "Ship Date", value: ({ ln }) => ((Number(ln.qty_shipped) || 0) > 0 ? xlsxDate(ln.dis_date) : "") },
  ];

  return (
    <Layout breadcrumb={["Home", "Open Orders"]}>
      <div className="jde-page">
        <style>{`
          .oo-filterbar { display: flex; flex-wrap: wrap; align-items: center; gap: 8px; margin-bottom: 12px;
            background: var(--omh-bg-panel); border: 1px solid var(--omh-divider); border-radius: var(--omh-radius-sm); padding: 8px 10px; }
          .oo-filterbar .jde-input, .oo-filterbar .jde-select { height: 30px; font-size: 12px; padding: 4px 10px; width: auto; }
          .oo-filterbar .jde-select { padding-right: 26px; background-position: right 8px center; min-width: 118px; max-width: 190px; }
          .oo-filterbar .jde-btn { height: 30px; padding: 0 10px; font-size: 12px; }
          .oo-filter-search { display: flex; align-items: center; gap: 6px; flex: 1 1 220px; min-width: 190px;
            background: #fff; border: 1px solid var(--omh-border-strong); border-radius: var(--omh-radius-sm);
            height: 30px; padding: 0 10px; transition: border-color 0.15s, box-shadow 0.15s; }
          .oo-filter-search:focus-within { border-color: var(--omh-primary); box-shadow: 0 0 0 3px rgba(29,78,216,0.12); }
          .oo-filter-search input { border: 0; outline: none; background: transparent; width: 100%; font-size: 12px; color: var(--omh-text); font-family: inherit; }
          .oo-daterange { display: flex; align-items: center; gap: 6px; }
          .oo-daterange input[type=date] { width: 132px; }
          .oo-divider { width: 1px; align-self: stretch; background: var(--omh-divider); margin: 0 2px; }
          .oo-toggle { display: inline-flex; align-items: center; gap: 6px; font-size: 12px; font-weight: 600; cursor: pointer; color: var(--omh-text-muted); white-space: nowrap; }
        `}</style>
        <div className="oo-filterbar" data-testid="oo-filterbar">
          <div className="oo-filter-search">
            <MagnifyingGlass size={13} weight="bold" style={{ color: "var(--omh-text-muted)", flex: "0 0 auto" }} />
            <input
              placeholder="Search PO / customer / item…"
              value={search}
              onChange={(e) => setParam("q", e.target.value)}
              data-testid="search-input"
            />
          </div>
          <select className="jde-select" value={plant} onChange={(e) => setParam("plant", e.target.value)} data-testid="filter-plant" title="Plant">
            <option value="">All plants</option>
            {plants.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
          <select className="jde-select" value={customer} onChange={(e) => setParam("customer", e.target.value)} data-testid="filter-customer" title="Customer">
            <option value="">All customers</option>
            {customers.map((c, i) => <option key={`${c.ms_pcode || c.name}-${i}`} value={c.ms_pcode || ""}>{`${c.name}${c.ms_pcode ? ` (${c.ms_pcode})` : ""}`}</option>)}
          </select>
          <div className="oo-daterange" title="PO date range">
            <input type="date" className="jde-input" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} data-testid="oo-datefrom" title="PO date from" />
            <span className="muted" style={{ fontSize: 11 }}>→</span>
            <input type="date" className="jde-input" value={dateTo} onChange={(e) => setDateTo(e.target.value)} data-testid="oo-dateto" title="PO date to" />
            {(dateFrom || dateTo) && (
              <button className="jde-btn" onClick={() => { setDateFrom(""); setDateTo(""); }} data-testid="oo-dateclear" title="Clear dates">
                <X size={11} weight="bold" />
              </button>
            )}
          </div>
          <div className="oo-divider" />
          <button className="jde-btn" onClick={expandAll} data-testid="expand-all">Expand all</button>
          <button className="jde-btn" onClick={collapseAll} data-testid="collapse-all">Collapse all</button>
          <ExportXlsxButton filename="open-orders-items" columns={exportColumns} rows={exportRows} sheetName="Open Order Items" testid="oo-export-xlsx" />
          <label className="oo-toggle" data-testid="oo-group-toggle" style={{ marginLeft: "auto" }}>
            <input type="checkbox" checked={groupByCustPo} onChange={(e) => setGroupByCustPo(e.target.checked)} data-testid="oo-group-checkbox" />
            Group by Customer PO
          </label>
        </div>

        {/* Summary line */}
        {!initialLoaded ? (
          <div className="jde-panel" data-testid="oo-loading">
            <div className="jde-panel-body" style={{ padding: 56, textAlign: "center", color: "var(--omh-text-muted)", display: "flex", flexDirection: "column", alignItems: "center", gap: 12 }}>
              <ArrowsClockwise size={24} weight="bold" className="oo-spin" style={{ color: "var(--omh-primary)" }} />
              <div style={{ fontSize: 13, fontWeight: 600 }}>Loading open orders…</div>
              <style>{`@keyframes oo-spin{to{transform:rotate(360deg)}}.oo-spin{animation:oo-spin 0.9s linear infinite}`}</style>
            </div>
          </div>
        ) : (
        <>
        <div style={{
          background: "white", border: "1px solid var(--jde-border)", padding: "10px 14px",
          fontSize: 12, marginBottom: 12, display: "flex", gap: 24, fontFamily: "var(--jde-font-mono)",
          flexWrap: "wrap", alignItems: "center",
        }} data-testid="open-orders-summary">
          <div><span className="muted">POs:</span> <strong>{fmtNum(view.length)}</strong></div>
          <div><span className="muted">Open lines:</span> <strong>{fmtNum(totalLines)}</strong></div>
          <div><span className="muted">Total open qty:</span> <strong>{fmtNum(totalOpen)}</strong></div>
          <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
            <span className="muted">Total open value:</span>
            {currencyTotals.length === 0 ? (
              <strong>—</strong>
            ) : (
              currencyTotals.map(([cur, val]) => (
                <strong key={cur} data-testid={`open-value-${cur}`}>{fmtCur(val, cur)}</strong>
              ))
            )}
          </div>
        </div>

        <div className="jde-panel">
          <div className="jde-panel-body" style={{ padding: 0 }}>
            <table className="jde-grid" data-testid="open-orders-table">
              <thead>
                <tr>
                  <th style={{ width: 28 }}></th>
                  <SortTh k="ms_custpo">Customer PO</SortTh>
                  <SortTh k="po_date">PO Date</SortTh>
                  <SortTh k="cust_po2">End Customer PO</SortTh>
                  <SortTh k="customer_name">Customer</SortTh>
                  <SortTh k="lines" align="right">Releases</SortTh>
                  <SortTh k="tot_ordered" align="right">Ordered</SortTh>
                  <SortTh k="tot_shipped" align="right">Shipped</SortTh>
                  <SortTh k="tot_open" align="right">Open</SortTh>
                  <SortTh k="tot_value" align="right">Open Value</SortTh>
                </tr>
              </thead>
              <tbody>
                {view.map((g) => {
                  const open = expanded.has(g.group_id);
                  const single = g.ponos.size === 1;
                  const glines = [...g.lines].sort((a, b) => String(a.deliv_date || "").localeCompare(String(b.deliv_date || "")));
                  return (
                    <Fragment key={g.group_id}>
                      <tr
                        key={`g-${g.group_id}`}
                        onClick={() => toggle(g.group_id)}
                        style={{ cursor: "pointer", background: open ? "#F0F7FF" : undefined }}
                        data-testid={`oo-row-${g.group_id}`}
                      >
                        <td>{open ? <CaretDown size={12} weight="bold" /> : <CaretRight size={12} weight="bold" />}</td>
                        <td className="text-mono">
                          <strong style={{ color: "var(--omh-primary)" }}>{g.ms_custpo || "—"}</strong>
                          {!single && (
                            <span className="jde-badge" data-testid={`oo-releases-${g.group_id}`}
                              style={{ marginLeft: 6, background: "#E0E7FF", color: "#3730A3", fontWeight: 700, fontSize: 10 }}>
                              {g.ponos.size} releases
                            </span>
                          )}
                          {g._source === "intake" && (
                            <span className="jde-badge" data-testid={`oo-intake-${g.group_id}`}
                              style={{ marginLeft: 6, background: "#EDE9FE", color: "#5B21B6", borderColor: "#C4B5FD" }}>
                              Intake
                            </span>
                          )}
                          {g.rev_no > 0 && <span className="jde-badge partial" style={{ marginLeft: 6 }}>R{g.rev_no}</span>}
                          {(() => { const le = latestLineEdit({ lines: g.lines }); return le.edit
                            ? <span style={{ marginLeft: 6 }}><EditedPill edit={le.edit} count={le.count} testid={`oo-row-edited-${g.group_id}`} /></span>
                            : null; })()}
                        </td>
                        <td className="text-mono" style={{ fontSize: 11 }}>{fmtDate(g.po_date)}</td>
                        <td className="text-mono" style={{ fontSize: 11 }}>{g.cust_po2 || <span className="muted">—</span>}</td>
                        <td>{g.customer_name}</td>
                        <td className="text-mono" style={{ textAlign: "right" }}>{glines.length}</td>
                        <td className="text-mono" style={{ textAlign: "right" }}>{fmtNum(g.tot_ordered)}</td>
                        <td className="text-mono" style={{ textAlign: "right", color: "#0F766E" }}>{fmtNum(g.tot_shipped)}</td>
                        <td className="text-mono" style={{ textAlign: "right", fontWeight: 700, color: "#B91C1C" }}>{fmtNum(g.tot_open)}</td>
                        <td className="text-mono" style={{ textAlign: "right", fontWeight: 600 }}>{fmtCur(g.tot_value, g.currency_code)}</td>
                      </tr>
                      {open && glines.map((ln, i) => (
                        <tr key={`l-${g.group_id}-${i}`} style={{ background: "#FAFBFC" }} data-testid={`oo-line-${g.group_id}-${i}`}>
                          <td></td>
                          <td className="text-mono" style={{ fontSize: 11 }}>
                            <span style={{ paddingLeft: 16 }}>
                              <span className="jde-badge" style={{ background: "#E0F2FE", color: "#075985", fontSize: 9, fontWeight: 700 }}>R{i + 1}</span>
                              <span style={{ marginLeft: 6 }}>due {fmtDate(ln.deliv_date)}</span>
                              {targetFromDue(ln.deliv_date, ln.offset_days) && (
                                <span style={{ marginLeft: 8, color: "#B45309", fontWeight: 600 }}
                                      title={ln.offset_days ? `Customer due − ${ln.offset_days}d ex-factory offset` : "No offset configured (= due date)"}
                                      data-testid={`oo-line-target-${g.group_id}-${i}`}>
                                  target {fmtDate(targetFromDue(ln.deliv_date, ln.offset_days))}
                                  {(Number(ln.offset_days) || 0) > 0 && <span className="muted" style={{ marginLeft: 3, fontSize: 9 }}>(−{ln.offset_days}d)</span>}
                                </span>
                              )}
                            </span>
                          </td>
                          <td style={{ fontSize: 11 }}>
                            <span className="text-mono" style={{ fontWeight: 600 }}>{ln.icode}</span>
                            <span className="muted" style={{ marginLeft: 6 }}>{ln.name}</span>
                            <WmPart icode={ln.icode} />
                            {ln.last_edit && <span style={{ marginLeft: 6 }}><EditedPill edit={ln.last_edit} testid={`oo-line-edited-${g.group_id}-${i}`} /></span>}
                          </td>
                          <td></td>
                          <td style={{ fontSize: 11 }}>
                            {ln._source === "intake"
                              ? <Link to={`/po/${ln._intake_id}`} onClick={(e) => e.stopPropagation()} style={{ color: "var(--omh-primary)" }} data-testid={`oo-view-${g.group_id}-${i}`}>View / Edit →</Link>
                              : <button className="jde-btn jde-btn-sm" onClick={(e) => { e.stopPropagation(); openPoModal(ln.ms_pono); }} data-testid={`oo-view-${g.group_id}-${i}`}>View / Edit →</button>}
                          </td>
                          <td></td>
                          <td className="text-mono" style={{ textAlign: "right", fontSize: 11 }}>
                            {fmtNum(ln.qty_ordered)}
                          </td>
                          <td className="text-mono" style={{ textAlign: "right", fontSize: 11, color: "#0F766E" }}>
                            {fmtNum(ln.qty_shipped)}
                            {(Number(ln.qty_shipped) || 0) > 0 && ln.dis_date && (
                              <div style={{ fontSize: 9, color: "#64748B", fontWeight: 500 }} data-testid={`oo-line-shipdate-${g.group_id}-${i}`}>shipped {fmtDate(ln.dis_date)}</div>
                            )}
                          </td>
                          <td className="text-mono" style={{ textAlign: "right", fontSize: 11, fontWeight: 700 }}>{fmtNum(ln.qty_open)}</td>
                          <td className="text-mono" style={{ textAlign: "right", fontSize: 11 }}>
                            <div>{fmtCur(ln.open_value, ln.currency_code)}</div>
                            {ln._source !== "intake" && Number.isInteger(ln.line_index) && (
                              <div style={{ fontSize: 9, marginTop: 2, display: "inline-flex", gap: 3, alignItems: "center", justifyContent: "flex-end" }} onClick={(e) => e.stopPropagation()}>
                                <span className="muted">@ {fmtCur(ln.price, ln.currency_code)}</span>
                                <button className="jde-btn jde-btn-sm" style={{ padding: "0 3px" }} disabled={resyncKey === `${ln.ms_pono}-${ln.line_index}`}
                                  title="Push this line's due date, qty & price to the live ERP (retry if ERP was out of sync)"
                                  onClick={() => resyncLine(ln.ms_pono, ln.line_index)} data-testid={`oo-erp-sync-${g.group_id}-${i}`}><ArrowsClockwise size={10} /></button>
                              </div>
                            )}
                          </td>
                        </tr>
                      ))}
                    </Fragment>
                  );
                })}
                {!loading && view.length === 0 && (
                  <tr><td colSpan={10} style={{ textAlign: "center", padding: 24 }} className="muted">No open orders.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
        </>
        )}
      </div>
      <PoDetailModal po={viewPo} loading={viewLoading} onClose={() => { setViewPo(null); setViewLoading(false); }} />
    </Layout>
  );
}
